/**
 * THE COMPOSER — two fields on the wire, forever.
 *
 * 🔴 `TurnRequest` has exactly `session_id` and `question` (spec 12.4). There is
 * deliberately no `history` field and no parameter that could carry a number
 * back to the model, which is what closes the re-injection hole the full
 * precision toggle would otherwise open: the toggle is a CLIENT-SIDE view
 * switch, the revealed value is never appended to a transcript, and there is
 * nowhere to post it even if a future component tried.
 *
 * Enter submits; Shift+Enter is a newline. The composer is enabled immediately
 * on an empty screen — the starters are an offer, never a gate.
 */
import { useState, type FormEvent, type KeyboardEvent } from 'react';
import { Send, Square } from 'lucide-react';

/** Far below the gate's 2,000-character refusal; the gate stays the decider. */
const SOFT_LIMIT = 2_000;

export function Composer({
  onAsk,
  onStop,
  busy,
  value,
  onChange,
  variant = 'docked',
}: {
  onAsk: (question: string) => void;
  onStop: () => void;
  busy: boolean;
  value: string;
  onChange: (next: string) => void;
  /**
   * `hero` on the empty screen — the composer IS the page, so it is tall and
   * centred. `docked` once a conversation exists — it steps back to a single
   * line at the foot of the thread. Same control, same handlers; only the
   * height and the hint change.
   */
  variant?: 'hero' | 'docked';
}) {
  const hero = variant === 'hero';
  const [touched, setTouched] = useState(false);
  const tooLong = value.length > SOFT_LIMIT;

  const submit = (event: FormEvent) => {
    event.preventDefault();
    setTouched(true);
    if (busy || tooLong || value.trim().length === 0) return;
    onAsk(value.trim());
  };

  const onKeyDown = (event: KeyboardEvent<HTMLTextAreaElement>) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      submit(event as unknown as FormEvent);
    }
  };

  return (
    <form
      onSubmit={submit}
      className={`rounded-2xl border bg-card transition-shadow ${hero ? 'p-4 shadow-lg shadow-black/20' : 'p-4'}`}
    >
      <label htmlFor="agent-composer" className="sr-only">
        Ask a question about the business
      </label>
      <textarea
        id="agent-composer"
        value={value}
        onChange={(event) => onChange(event.target.value)}
        onKeyDown={onKeyDown}
        rows={hero ? 3 : 1}
        // This used to read "each one is answered on its own", and it used to be
        // TRUE: the worker opened every turn with an empty transcript, so "and
        // by state?" had no antecedent and the model either declined or guessed
        // a metric and answered a question nobody asked.
        //
        // A follow-up now resolves. The turn runner loads this session's earlier
        // exchanges from the SERVER's own record and puts them on the clearance
        // (`transport/session-context.ts`). The transport is still two fields
        // forever and `ModelMessage` still has no `user` variant — the client
        // cannot post a transcript, and none of that changed. What changed is
        // that the server supplies one it already screened.
        //
        // 🔴 SO THE PLACEHOLDER HAD TO CHANGE WITH IT. A sentence that was
        // accurate when it was written and is now false is worse than no
        // sentence: it is the interface telling the operator not to try the
        // thing that works, in the product's own voice.
        placeholder={
          hero
            ? 'Ask about members, matches, moderation, revenue…'
            : 'Ask a follow-up — this conversation is remembered'
        }
        aria-invalid={tooLong || undefined}
        aria-describedby={tooLong ? 'agent-composer-error' : undefined}
        className={`w-full resize-y bg-transparent outline-none placeholder:text-muted-foreground ${hero ? 'text-base' : 'text-[15px]'}`}
      />
      <div className="mt-2 flex items-center justify-between gap-3">
        <span
          id="agent-composer-error"
          className={`text-[11px] ${tooLong ? 'text-red-600 dark:text-red-400' : 'text-muted-foreground'}`}
        >
          {tooLong
            ? `${value.length} characters — trim to ${SOFT_LIMIT} or fewer.`
            : hero
              ? // True with and without the row-level SQL tools: the input gate refuses a full
                // name, an e-mail address or a phone number in every environment (input-gate.ts).
                'Members, matches and trends — never a full name, e-mail or phone'
              : 'Enter to send · Shift+Enter for a new line'}
        </span>
        <div className="flex items-center gap-2">
          {busy && (
            <button
              type="button"
              onClick={onStop}
              className="inline-flex items-center gap-1 rounded-md border px-2.5 py-1.5 text-xs transition-colors hover:bg-muted"
            >
              <Square className="h-3 w-3" aria-hidden />
              Stop
            </button>
          )}
          <button
            type="submit"
            disabled={busy || tooLong || value.trim().length === 0}
            className="inline-flex items-center gap-1 rounded-md bg-primary px-3 py-1.5 text-xs font-medium text-primary-foreground transition-opacity disabled:opacity-50"
          >
            <Send className="h-3 w-3" aria-hidden />
            Ask
          </button>
        </div>
      </div>
      {touched && value.trim().length === 0 && (
        <p className="mt-1 text-[11px] text-muted-foreground">Type a question first.</p>
      )}
    </form>
  );
}
